import React, { useState, useEffect } from 'react';
import { makeAuthenticatedRequest } from '../../core/api/client';

const stageBadgeClass = (stage) => {
  const s = String(stage || '').toUpperCase();
  if (s.includes('WON')) return 'bg-green-100 text-green-700';
  if (s.includes('LOST')) return 'bg-red-100 text-red-700';
  if (s.includes('OFFER')) return 'bg-blue-100 text-blue-700';
  return 'bg-orange-100 text-orange-700';
};

const CustomerHistoryTab = ({ accountId }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterGrade, setFilterGrade] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [showUnconverted, setShowUnconverted] = useState(false);

  useEffect(() => {
    if (!accountId) return;

    let cancelled = false;

    const loadHistory = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await makeAuthenticatedRequest(
          `/api/v1/enquiries/customer-history?account_id=${accountId}`
        );
        if (!response.ok) throw new Error('Failed to load customer history');
        const history = await response.json();
        if (!cancelled) setData(history);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load customer history. Please try again.');
          setData(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadHistory();
    return () => { cancelled = true; };
  }, [accountId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
        Loading customer history...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
    );
  }

  if (!data) return null;

  const allPrices = data.prices || [];
  // "What they buy" — aggregate by grade+size across all this customer's enquiries
  const buyMap = {};
  (data.prices || []).forEach((r) => {
    const key = `${r.grade || '?'} / ${r.size_mm || '?'}`;
    if (!buyMap[key]) buyMap[key] = { grade: r.grade, size: r.size_mm, count: 0, last: null };
    buyMap[key].count += 1;
    const d = (r.date || '').slice(0,10);
    if (d && (!buyMap[key].last || d > buyMap[key].last)) buyMap[key].last = d;
  });
  const whatTheyBuy = Object.entries(buyMap)
    .map(([label, v]) => ({ label, ...v }))
    .sort((a,b) => b.count - a.count)
    .slice(0, 8);
  const prices = allPrices.filter((r) => {
    if (filterGrade && r.grade !== filterGrade) return false;
    const d = (r.date || '').slice(0, 10);
    if (filterFrom && d && d < filterFrom) return false;
    if (filterTo && d && d > filterTo) return false;
    return true;
  });
  const matrix = data.matrix || [];
  const pipeline = data.pipeline || [];

  const uniqueGrades = [...new Set(matrix.map((r) => r.grade).filter(Boolean))];
  const wonDeals = pipeline.filter((p) => (p.stage || '').includes('WON')).length;
  const unconvertedDeals = pipeline.filter((p) => {
    const st = (p.stage || p.Stage || '').toUpperCase();
    return !st.includes('WON');
  });
  const totalValue = pipeline.reduce((sum, p) => sum + (parseFloat(p.deal_value) || 0), 0);

  if (!prices.length && !matrix.length && !pipeline.length) {
    return (
      <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
        <div className="text-3xl mb-2">📭</div>
        <p className="text-sm text-gray-500">No history data found for this customer.</p>
      </div>
    );
  }

  const stats = [
    { label: 'Grades Enquired', value: uniqueGrades.length, icon: '🔬' },
    { label: 'Prices on Record', value: prices.length, icon: '💰' },
    { label: 'Pipeline Deals', value: pipeline.length, icon: '📈' },
    { label: 'Deals Won', value: wonDeals, icon: '🏆' },
    { label: 'Conversion (won/total)', value: `${wonDeals} / ${pipeline.length}`, icon: '🎯', clickable: true },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map(({ label, value, icon, clickable }) => (
          <div key={label}
            onClick={clickable ? () => setShowUnconverted((v) => !v) : undefined}
            className={`bg-white border rounded-lg p-4 ${clickable ? 'cursor-pointer border-blue-300 hover:bg-blue-50' : 'border-gray-200'}`}>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{icon} {label}</p>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {clickable && <p className="text-xs text-blue-600 mt-1">{showUnconverted ? 'Hide' : 'Click to see unconverted'}</p>}
          </div>
        ))}
      </div>

      {showUnconverted && (
        <div className="bg-white border border-blue-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-blue-50">
            <h3 className="text-sm font-semibold text-gray-900">Unconverted deals ({unconvertedDeals.length})</h3>
            <p className="text-xs text-gray-500">Enquiries that haven't become a won order — follow these up.</p>
          </div>
          {unconvertedDeals.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-400">All deals converted.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Stage', 'Deal Value', 'SC', 'Expected Closure'].map((h) => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {unconvertedDeals.map((d, i) => {
                    const stage = d.stage || d.Stage || '';
                    const dv = d.deal_value || d.DealValue;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2"><span className="rounded px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700">{stage || '—'}</span></td>
                        <td className="px-4 py-2 font-semibold text-gray-700">{dv ? `₹${Number(dv).toLocaleString()}` : '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{d.sc || d.SalespersonName || '—'}</td>
                        <td className="px-4 py-2 text-gray-500">{(d.expected_closure_date || d.ExpectedClosureDate)?.slice(0,10) || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {whatTheyBuy.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">🛒 What They Buy</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {whatTheyBuy.map((item) => (
              <div key={item.label} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md text-sm">
                <span className="font-medium text-gray-800">{item.label}</span>
                <span className="text-xs text-gray-500">{item.count}×{item.last ? ` · last ${item.last}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 bg-white border border-gray-200 rounded-lg p-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Grade</label>
          <select value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded-md text-sm min-w-[120px]">
            <option value="">All grades</option>
            {uniqueGrades.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
        </div>
        {(filterGrade || filterFrom || filterTo) && (
          <button onClick={() => { setFilterGrade(''); setFilterFrom(''); setFilterTo(''); }}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
            Clear
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto self-center">{prices.length} of {allPrices.length} records</span>
      </div>

      {prices.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">💰 Price History</h3>
            <span className="text-xs text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">{prices.length} records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Grade', 'Size', 'Price', 'Unit', 'Qty', 'UOM'].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {prices.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500">{r.date?.slice(0, 10) || '—'}</td>
                    <td className="px-4 py-2">
                      <span className="bg-blue-50 text-blue-700 rounded px-2 py-0.5 text-xs font-semibold">{r.grade || '—'}</span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{r.size_mm || '—'}</td>
                    <td className="px-4 py-2 font-bold text-orange-600">{r.price_offer || '—'}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{r.price_uom || '—'}</td>
                    <td className="px-4 py-2 text-gray-700">{r.qty || '—'}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{r.uom || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {matrix.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">📋 Grade Demand by Month</h3>
            <span className="text-xs text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">{uniqueGrades.length} grades</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Month', 'Grade', 'No. of Enquiries', 'Total Qty'].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {matrix.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-500">{r.month || 'No date'}</td>
                    <td className="px-4 py-2">
                      <span className="bg-green-50 text-green-700 rounded px-2 py-0.5 text-xs font-semibold">{r.grade || '—'}</span>
                    </td>
                    <td className="px-4 py-2 font-semibold text-gray-900">{r.enquiry_count}</td>
                    <td className="px-4 py-2 text-gray-700">
                      {r.total_qty ? `${r.total_qty.toFixed(1)} ${r.uom || ''}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pipeline.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">📈 Pipeline</h3>
            {totalValue > 0 && (
              <span className="text-xs font-semibold text-green-700 bg-green-50 rounded-full px-2 py-0.5">
                ₹{totalValue.toLocaleString()} total value
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Stage', 'Deal Value', 'SC', 'Expected Closure', 'Lead Source'].map((h) => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pipeline.map((d, i) => {
                  const stage = d.stage || d.Stage || '';
                  const dealValue = d.deal_value || d.DealValue;
                  return (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${stageBadgeClass(stage)}`}>{stage || '—'}</span>
                      </td>
                      <td className="px-4 py-2 font-bold text-green-700">
                        {dealValue ? `₹${Number(dealValue).toLocaleString()}` : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-700">{d.sc || d.SalespersonName || '—'}</td>
                      <td className="px-4 py-2 text-gray-500">
                        {(d.expected_closure_date || d.ExpectedClosureDate)?.slice(0, 10) || '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-500">{d.lead_source || d.LeadSource || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerHistoryTab;
